import type { AccountInfo, SignInPending, SignInResponse } from '../../../shared/protocol.ts';
import { api, ApiError, SESSION_KEY } from './api.ts';
import { Observable } from './emitter.ts';

const ACCOUNT_KEY = 'takehouse.account';

/**
 * Who is signed in on this browser. People are known by email address alone (no passwords): signing in with
 * the same email on any device brings back the same studios and recordings.
 */
class AccountStore extends Observable {
  account: AccountInfo | null = null;
  /** The server has confirmed (or rejected) the remembered sign-in. */
  checked = false;

  constructor() {
    super();
    try {
      this.account = JSON.parse(localStorage.getItem(ACCOUNT_KEY) ?? 'null');
    } catch {}
    if (this.account && localStorage.getItem(SESSION_KEY)) void this.refresh();
    else this.checked = true;
  }

  get signedIn() {
    return !!this.account;
  }

  /** Re-reads the account; a session the server no longer knows signs this browser out. */
  async refresh() {
    try {
      this.save(await api.me());
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) this.clear();
    }
    this.checked = true;
    this.changed();
  }

  /**
   * Signs in at once, or, on a server that checks addresses, emails a code and returns the pending state;
   * finish with verify().
   */
  async signIn(email: string, name?: string): Promise<AccountInfo | SignInPending> {
    const r = await api.signIn(email, name);
    if ('pending' in r) return r;
    return this.accept(r);
  }

  async verify(email: string, code: string) {
    return this.accept(await api.verifySignIn(email, code));
  }

  private accept(r: SignInResponse) {
    localStorage.setItem(SESSION_KEY, r.token);
    this.save(r.account);
    if (r.account.name && !localStorage.getItem('takehouse.name')) localStorage.setItem('takehouse.name', r.account.name);
    this.checked = true;
    this.changed();
    return r.account;
  }

  async rename(name: string) {
    this.save(await api.rename(name));
    this.changed();
  }

  /** Registers interest in creating studios on an invitation-only site. */
  async requestAccess(req: Parameters<typeof api.requestAccess>[0]) {
    this.save(await api.requestAccess(req));
    this.changed();
  }

  async signOut() {
    await api.signOut().catch(() => {});
    this.clear();
    this.changed();
  }

  /** Ends this person's sessions on every browser, this one included. */
  async signOutEverywhere() {
    await api.signOutEverywhere();
    this.clear();
    this.changed();
  }

  private save(a: AccountInfo) {
    this.account = a;
    try {
      localStorage.setItem(ACCOUNT_KEY, JSON.stringify(a));
    } catch {}
  }

  private clear() {
    this.account = null;
    try {
      localStorage.removeItem(ACCOUNT_KEY);
      localStorage.removeItem(SESSION_KEY);
    } catch {}
  }
}

export const account = new AccountStore();

export const isEmail = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());
